// TV live remote-control manager (Pro masjids only).
//
// Replaces the old SSE EventSource transport with a Firestore onSnapshot
// listener on masjids/{masjidId}/tvControl/state. The public surface is
// preserved so call sites in TeleView.jsx / PrayerTimesView.jsx keep working:
//
//   acquireTvSse / releaseTvSse
//   addTvCommandListener / addTvStatusListener
//   updateTvSettings / updateTvLastView / updateTvOverlayState
//
// See Website/CLAUDE.md "Documented exception" for why this file is allowed
// to read Firestore directly.

import { doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { db as firestore } from "../config/firebase";

const commandListeners = new Set();
const statusListeners = new Set();

let currentParams = null;
let clientId = null;
let registering = false;
let unsubscribeSnapshot = null;
let lastAppliedNonce = null;
let initialSnapshotConsumed = false;

let registerRetryTimeout = null;
let closeTimeout = null;
let refCount = 0;
let beforeUnloadBound = false;

const CLOSE_GRACE_MS = 5000;
const REGISTER_BASE_DELAY_MS = 3000;
const REGISTER_MAX_DELAY_MS = 30000;
let registerAttemptCount = 0;

const CLIENT_UPDATE_DEBOUNCE_MS = 400;
let clientUpdateTimeout = null;
let pendingClientUpdate = null;

// Liveness heartbeat written DIRECTLY to Firestore (not via the Cloud Function).
// The backend prunes clients whose lastActivity is older than 90s; an idle TV
// otherwise never refreshes it after registering. We write straight to the
// client doc to avoid invoking the heavy masjid function on a 24/7 timer.
// 60s gives a 2.5x margin under the backend's 150s stale window.
const HEARTBEAT_INTERVAL_MS = 60000;
let heartbeatTimer = null;

// Set once a 403 isPremium is seen (masjid is not/no longer Pro). Latches off ALL
// network activity until the page reloads, so a mid-session downgrade can't loop
// the backend with rejected register/client-update calls.
let premiumDisabled = false;

const notifyStatus = (status) => {
  statusListeners.forEach((listener) => {
    try {
      listener(status);
    } catch (err) {
      console.error("TV live status listener error:", err);
    }
  });
};

const notifyCommand = (data) => {
  commandListeners.forEach((listener) => {
    try {
      listener(data);
    } catch (err) {
      console.error("TV live command listener error:", err);
    }
  });
};

const sendUnregisterBeacon = () => {
  if (!currentParams?.masjidApi || !currentParams?.masjidId || !clientId) return;
  const url = `${currentParams.masjidApi}/v1/tv/unregister-client/${currentParams.masjidId}`;
  const body = JSON.stringify({ clientId, sessionId: currentParams.sessionId });
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(url, blob);
  } else {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch((err) => console.error("TV live unregister beacon failed", err));
  }
};

const teardownSnapshot = () => {
  if (unsubscribeSnapshot) {
    try { unsubscribeSnapshot(); } catch (e) { /* ignore */ }
    unsubscribeSnapshot = null;
  }
  initialSnapshotConsumed = false;
  lastAppliedNonce = null;
};

const stopHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
};

const sendHeartbeat = async () => {
  if (!clientId || !currentParams?.masjidId) return;
  try {
    const ref = doc(firestore, "masjids", currentParams.masjidId, "tvClients", clientId);
    await updateDoc(ref, { lastActivity: serverTimestamp() });
  } catch (err) {
    // Doc was pruned (e.g. after the device slept past the stale window) —
    // drop this session and re-register to recreate it via the backend.
    if (err?.code === "not-found") {
      stopHeartbeat();
      teardownSnapshot();
      clientId = null;
      if (refCount > 0 && currentParams) register();
      return;
    }
    if (err?.code === "permission-denied") {
      // Masjid is no longer Pro — the rule now denies the write. Latch off so the
      // heartbeat (and all other activity) stops within one interval.
      disableForPremium();
      return;
    }
    console.warn("TV live: heartbeat failed", err?.message || err);
  }
};

const startHeartbeat = () => {
  stopHeartbeat();
  if (!clientId) return;
  heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
};

const closeConnection = () => {
  stopHeartbeat();
  if (registerRetryTimeout) {
    clearTimeout(registerRetryTimeout);
    registerRetryTimeout = null;
  }
  registerAttemptCount = 0;
  if (clientUpdateTimeout) {
    clearTimeout(clientUpdateTimeout);
    clientUpdateTimeout = null;
  }
  pendingClientUpdate = null;
  teardownSnapshot();
  sendUnregisterBeacon();
  clientId = null;
  notifyStatus({ connected: false, clientId: null });
};

// Latch off all network activity after a 403 isPremium (masjid not/no longer Pro).
// Stays off until the page reloads — prevents a downgraded-mid-session TV from
// looping the backend with rejected calls.
const disableForPremium = () => {
  premiumDisabled = true;
  stopHeartbeat();
  if (registerRetryTimeout) {
    clearTimeout(registerRetryTimeout);
    registerRetryTimeout = null;
  }
  if (clientUpdateTimeout) {
    clearTimeout(clientUpdateTimeout);
    clientUpdateTimeout = null;
  }
  pendingClientUpdate = null;
  teardownSnapshot();
  clientId = null;
  notifyStatus({ connected: false, clientId: null, reason: "non-pro" });
};

const scheduleClose = () => {
  if (closeTimeout) return;
  closeTimeout = setTimeout(() => {
    closeTimeout = null;
    if (refCount === 0) {
      closeConnection();
      if (beforeUnloadBound) {
        window.removeEventListener("beforeunload", sendUnregisterBeacon);
        beforeUnloadBound = false;
      }
    }
  }, CLOSE_GRACE_MS);
};

const handleSnapshot = (snap) => {
  if (!snap.exists()) {
    // No control doc yet — record an empty baseline so the first real command applies.
    if (!initialSnapshotConsumed) initialSnapshotConsumed = true;
    return;
  }

  const data = snap.data() || {};
  const nonce = data.nonce || null;

  if (!initialSnapshotConsumed) {
    // Skip the initial snapshot to avoid re-applying old commands on every reconnect.
    initialSnapshotConsumed = true;
    lastAppliedNonce = nonce;
    return;
  }

  if (!nonce || nonce === lastAppliedNonce) return;
  lastAppliedNonce = nonce;

  const target = data.targetClientId;
  if (target && clientId && target !== clientId) return;

  const payload = (data.payload && typeof data.payload === "object") ? data.payload : {};
  notifyCommand({ command: data.command, ...payload, sentBy: data.sentBy || null });
};

const handleSnapshotError = (err) => {
  console.warn("TV live: snapshot error (will re-register)", err?.message || err);
  notifyStatus({ connected: false, clientId });
  teardownSnapshot();
  if (refCount === 0 || registerRetryTimeout || !currentParams) return;
  scheduleRegisterRetry();
};

const scheduleRegisterRetry = () => {
  if (registerRetryTimeout) return;
  const delayMs = Math.min(
    REGISTER_BASE_DELAY_MS * Math.pow(2, registerAttemptCount),
    REGISTER_MAX_DELAY_MS
  );
  registerAttemptCount += 1;
  registerRetryTimeout = setTimeout(() => {
    registerRetryTimeout = null;
    register();
  }, delayMs);
};

const register = async () => {
  if (premiumDisabled) return;
  if (registering || !currentParams || refCount === 0) return;
  if (unsubscribeSnapshot) return;

  registering = true;
  try {
    const {
      masjidApi,
      masjidId,
      sessionId,
      deviceName,
      initialView,
      getToken,
    } = currentParams;

    if (!masjidId || !masjidApi || !getToken) return;

    const settings = {
      themeMode: currentParams.themeMode,
      primaryColor: currentParams.primaryColor,
      showTomorrow: currentParams.showTomorrow,
      zoomLevel: currentParams.zoomLevel,
      adhanEnabled: currentParams.adhanEnabled,
      iqamaEnabled: currentParams.iqamaEnabled,
      adhanCountdownMinutes: currentParams.adhanCountdownMinutes,
      iqamaCountdownMinutes: currentParams.iqamaCountdownMinutes,
      adhanScreenDurationMinutes: currentParams.adhanScreenDurationMinutes,
      iqamaScreenDurationMinutes: currentParams.iqamaScreenDurationMinutes,
      jummahSilenceEnabled: currentParams.jummahSilenceEnabled,
      jummahSilenceDurationMinutes: currentParams.jummahSilenceDurationMinutes,
      forbiddenScreenEnabled: currentParams.forbiddenScreenEnabled,
      forbiddenScreenDurationMinutes: currentParams.forbiddenScreenDurationMinutes,
      tomorrowChangesBanner: currentParams.tomorrowChangesBanner,
      overlayActive: currentParams.overlayActive === true,
    };

    const token = await getToken(registerAttemptCount > 0);
    if (!token) {
      scheduleRegisterRetry();
      return;
    }

    const resp = await fetch(`${masjidApi}/v1/tv/register-client/${masjidId}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ sessionId, deviceName, initialView, settings }),
    });

    if (resp.status === 403) {
      const body = await resp.json().catch(() => ({}));
      if (body?.isPremium) {
        // Non-Pro (or downgraded mid-session) — latch off all activity until reload.
        disableForPremium();
        return;
      }
      console.warn("TV live: register denied (403)", body?.error);
      return;
    }

    if (!resp.ok) {
      scheduleRegisterRetry();
      return;
    }

    const body = await resp.json();
    if (!body?.clientId) {
      scheduleRegisterRetry();
      return;
    }

    clientId = body.clientId;
    registerAttemptCount = 0;
    initialSnapshotConsumed = false;
    lastAppliedNonce = null;

    const ref = doc(firestore, "masjids", masjidId, "tvControl", "state");
    unsubscribeSnapshot = onSnapshot(ref, handleSnapshot, handleSnapshotError);

    notifyStatus({ connected: true, clientId });
    startHeartbeat();
    flushClientUpdate();
  } catch (err) {
    console.warn("TV live: register failed", err?.message || err);
    scheduleRegisterRetry();
  } finally {
    registering = false;
  }
};

export const acquireTvSse = (params) => {
  if (premiumDisabled) return;
  currentParams = { ...params };
  refCount += 1;

  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }

  if (!beforeUnloadBound) {
    window.addEventListener("beforeunload", sendUnregisterBeacon);
    beforeUnloadBound = true;
  }

  register();
};

const mergePendingUpdate = (partial) => {
  if (!pendingClientUpdate) {
    pendingClientUpdate = { ...partial };
    if (partial.settings) {
      pendingClientUpdate.settings = { ...partial.settings };
    }
    return;
  }

  if (partial.settings) {
    pendingClientUpdate.settings = {
      ...(pendingClientUpdate.settings || {}),
      ...partial.settings,
    };
  }

  if (partial.lastView !== undefined) pendingClientUpdate.lastView = partial.lastView;
  if (partial.overlayActive !== undefined) pendingClientUpdate.overlayActive = partial.overlayActive;
};

const scheduleClientUpdate = (partial) => {
  mergePendingUpdate(partial);

  if (clientUpdateTimeout) {
    clearTimeout(clientUpdateTimeout);
  }

  clientUpdateTimeout = setTimeout(() => {
    clientUpdateTimeout = null;
    flushClientUpdate();
  }, CLIENT_UPDATE_DEBOUNCE_MS);
};

const stripPendingSettings = (keys) => {
  if (!pendingClientUpdate?.settings) return;
  const nextSettings = { ...pendingClientUpdate.settings };
  let changed = false;
  for (const key of keys) {
    if (key in nextSettings) {
      delete nextSettings[key];
      changed = true;
    }
  }
  if (!changed) return;

  if (Object.keys(nextSettings).length === 0) {
    delete pendingClientUpdate.settings;
  } else {
    pendingClientUpdate.settings = nextSettings;
  }
};

const stripPendingView = () => {
  if (pendingClientUpdate && "lastView" in pendingClientUpdate) {
    delete pendingClientUpdate.lastView;
  }
};

const hasUpdateContent = (update) => {
  if (!update) return false;
  if (update.lastView !== undefined) return true;
  if (update.overlayActive !== undefined) return true;
  if (update.settings && Object.keys(update.settings).length > 0) return true;
  return false;
};

const scheduleClientUpdateRetry = () => {
  if (clientUpdateTimeout || refCount === 0 || !currentParams) return;
  clientUpdateTimeout = setTimeout(() => {
    clientUpdateTimeout = null;
    flushClientUpdate();
  }, CLIENT_UPDATE_DEBOUNCE_MS);
};

const restorePendingUpdate = (update) => {
  if (!hasUpdateContent(update)) return;
  mergePendingUpdate(update);
  scheduleClientUpdateRetry();
};

const flushClientUpdate = async () => {
  if (!pendingClientUpdate || !currentParams?.masjidApi || !currentParams?.masjidId || !currentParams?.getToken) {
    return;
  }
  // Wait for clientId before sending — register() will call us again.
  if (!clientId) return;

  const update = pendingClientUpdate;
  const payload = { clientId };
  if (currentParams.sessionId) payload.sessionId = currentParams.sessionId;
  if (update.lastView !== undefined) payload.lastView = update.lastView;
  if (update.overlayActive !== undefined) payload.overlayActive = update.overlayActive;
  if (update.settings && Object.keys(update.settings).length > 0) {
    payload.settings = update.settings;
  }

  if (!hasUpdateContent(payload)) {
    pendingClientUpdate = null;
    return;
  }

  pendingClientUpdate = null;

  try {
    const token = await currentParams.getToken(false);
    if (!token) {
      restorePendingUpdate(update);
      return;
    }

    const resp = await fetch(
      `${currentParams.masjidApi}/v1/tv/client-update/${currentParams.masjidId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (resp.ok) return;

    if (resp.status === 403) {
      // Downgraded mid-session — latch off so we stop hammering the backend.
      const body = await resp.json().catch(() => ({}));
      if (body?.isPremium) disableForPremium();
      return;
    }

    if (resp.status === 401 || resp.status === 404) {
      return;
    }

    restorePendingUpdate(update);
  } catch (err) {
    console.warn("TV live: client-update failed (will retry)", err?.message || err);
    restorePendingUpdate(update);
  }
};

// Update TV settings with debouncing to avoid excessive API calls.
// Batches rapid changes into a single update.
export const updateTvSettings = (settings, options = {}) => {
  if (premiumDisabled || !currentParams) return;

  const settingsKeys = [
    "themeMode", "primaryColor", "showTomorrow", "zoomLevel",
    "adhanEnabled", "iqamaEnabled", "adhanCountdownMinutes",
    "iqamaCountdownMinutes", "adhanScreenDurationMinutes", "iqamaScreenDurationMinutes",
    "jummahSilenceEnabled", "jummahSilenceDurationMinutes",
    "forbiddenScreenEnabled", "forbiddenScreenDurationMinutes",
    "tomorrowChangesBanner",
  ];

  const changedSettings = {};
  for (const key of settingsKeys) {
    if (settings[key] !== undefined && settings[key] !== currentParams[key]) {
      changedSettings[key] = settings[key];
    }
  }

  const changedKeys = Object.keys(changedSettings);
  if (changedKeys.length === 0) return;

  currentParams = { ...currentParams, ...changedSettings };

  if (options.suppressNetwork) {
    stripPendingSettings(changedKeys);
    return;
  }

  scheduleClientUpdate({ settings: changedSettings });
};

export const releaseTvSse = () => {
  refCount = Math.max(0, refCount - 1);
  if (refCount === 0) {
    scheduleClose();
  }
};

export const addTvCommandListener = (listener) => {
  commandListeners.add(listener);
  return () => commandListeners.delete(listener);
};

export const addTvStatusListener = (listener) => {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
};

export const updateTvLastView = async (view, options = {}) => {
  if (premiumDisabled || !currentParams || !view) return;

  if (currentParams.initialView !== view) {
    currentParams = { ...currentParams, initialView: view };
    if (options.suppressNetwork) {
      stripPendingView();
      return;
    }

    scheduleClientUpdate({ lastView: view });
  }
};

// Reports overlay active state to backend so the mobile remote can show/hide
// the dismiss button.
export const updateTvOverlayState = (overlayActive) => {
  if (premiumDisabled || !currentParams) return;

  if (currentParams.overlayActive === overlayActive) return;

  currentParams = { ...currentParams, overlayActive };

  scheduleClientUpdate({ overlayActive });
};
